// LeavePage.js
import React from 'react';
import { useAuth } from "components/ui/navigationGuard";
import AdminNavigation from "layouts/adminNavigation";
import useLeavePageLogic from 'hooks/useLeave';

const LeavePage = () => {
  const { user } = useAuth();
  
  // Use the custom hook to get all the state and functions
  const {
    isCollapsed,
    loading,
    settings,
    onlyActive,
    category,
    employeeType,
    editValues,
    setIsCollapsed,
    setOnlyActive,
    setCategory,
    setEmployeeType,
    setEditValues,
    fetchSettings,
    handleToggleActive,
    handleUpdateValue
  } = useLeavePageLogic();

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavigation />
        <div className="pt-16 flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-muted-foreground">Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation
        user={user}
        isCollapsed={isCollapsed}
        onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      />

      <main
        className={`pt-16 transition-all duration-200 ease-in-out ${
          isCollapsed ? "lg:pl-16" : "lg:pl-64"
        }`}
      >
        <div className="mx-auto p-6">
          <h1 className="text-3xl font-bold mb-6 text-gray-800"> Settings</h1>
        
          <div className="mb-6">
            <label
              htmlFor="employeeType"
              className="text-sm font-medium text-gray-700 mr-2"
            >
              Employee Type:
            </label>
            <select
              id="employeeType"
              value={employeeType}
              onChange={(e) => setEmployeeType(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-green-500"
            >
              <option value="permanent">Permanent</option>
              <option value="contract">Contract</option>
            </select>
          </div>

          <div className="flex gap-3 mb-6">
            {["leave", "salary"].map((tab) => (
              <button
                key={tab}
                onClick={() => setCategory(tab)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition ${
                  category === tab
                    ? "bg-blue-600 text-white shadow"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {tab.toUpperCase()}
              </button>
            ))}
          </div>

          <div className="flex items-center mb-6">
            <input
              id="activeFilter"
              type="checkbox"
              checked={onlyActive}
              onChange={() => setOnlyActive(!onlyActive)}
              className="mr-2 h-4 w-4"
            />
            <label htmlFor="activeFilter" className="text-sm text-gray-700">
              Show only active
            </label>
          </div>

          <div className="grid gap-6 md:grid-cols-3 lg:grid-cols-4">
            {settings.map((setting) => {
              const currentValue =
                employeeType === "permanent"
                  ? setting.permanent_value
                  : setting.contract_value;
              const isActive =
                employeeType === "permanent"
                  ? setting.permanent_active
                  : setting.contract_active;

              const isChanged =
                editValues[setting.id] !== String(currentValue || "");

              return (
                <div
                  key={setting.id}
                  className="bg-white rounded-xl shadow p-5 flex flex-col justify-between hover:shadow-md transition"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h2 className="font-semibold text-lg text-gray-800">
                        {setting.label}
                      </h2>

                      {/* Editable input */}
                      <div className="flex items-center space-x-2 mt-2">
                        <input
                          type="number"
                          value={editValues[setting.id] || ""}
                          onChange={(e) =>
                            setEditValues({
                              ...editValues,
                              [setting.id]: e.target.value,
                            })
                          }
                          disabled={!isActive}
                          className="border rounded px-2 py-1 text-sm w-24"
                        />
                        <button
                          onClick={() =>
                            handleUpdateValue(setting.id, editValues[setting.id])
                          }
                          disabled={!isChanged || !isActive}
                          className={`px-3 py-1 text-sm rounded ${
                            isChanged && isActive
                              ? "bg-blue-600 text-white hover:bg-blue-700"
                              : "bg-gray-300 text-gray-600 cursor-not-allowed"
                          }`}
                        >
                          Save
                        </button>
                      </div>

                      <p
                        className={`mt-2 text-xs font-semibold ${
                          isActive ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {isActive ? "Active" : "Inactive"}
                      </p>
                    </div>

                    {/* Active toggle checkbox */}
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={() => handleToggleActive(setting.id)}
                      className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
};

export default LeavePage;